import { env } from '@/env';
import { analysisLogger, isConsoleLogger, timelineLogger } from '@/lib/logger';
import { isBrowserLocalhost } from '@/lib/utils/runtime';
import { getRecordValue, isRecord } from '@/lib/utils/type-guards';

const EXTENDED_LOGGING_BOOLEAN_PATTERN = /^(1|true)$/i;

const isDateObject = (value: unknown): value is Date =>
  typeof value === 'object' && value !== null && Object.prototype.toString.call(value) === '[object Date]';

export function isExtendedLoggingEnabled(): boolean {
  if (globalThis.window === undefined) {
    return env.NODE_ENV !== 'production';
  }

  try {
    const parameters = new URLSearchParams(globalThis.location.search);
    if (parameters.has('extendedLogging')) {
      const raw = parameters.get('extendedLogging');
      const enabled = raw === null || raw === '' || EXTENDED_LOGGING_BOOLEAN_PATTERN.test(raw);
      globalThis.localStorage.setItem('extendedLogging', enabled ? 'true' : 'false');
      return enabled;
    }
  } catch {
    // Ignore URL parsing failures and fall back to other detection mechanisms
  }

  try {
    const stored = globalThis.localStorage.getItem('extendedLogging');
    if (stored === 'true') {
      return true;
    }
    if (stored === 'false') {
      return false;
    }
  } catch {
    // Access to localStorage might fail (e.g., in strict privacy modes)
  }

  const vercelEnvironment = env.NEXT_PUBLIC_VERCEL_ENV;
  if (vercelEnvironment && vercelEnvironment.length > 0) {
    return vercelEnvironment !== 'production';
  }

  return isBrowserLocalhost();
}

export function logTimelineDebug(...arguments_: unknown[]) {
  if (!isExtendedLoggingEnabled()) {
    return;
  }

  const [first, ...rest] = arguments_;
  const message = typeof first === 'string' ? first : 'Timeline debug';

  const payload = (() => {
    if (rest.length > 0) {
      if (rest.length === 1) {
        return normalizePayload(rest[0]);
      }
      return { args: rest } satisfies Record<string, unknown>;
    }
    if (typeof first === 'string') {
      return;
    }
    return normalizePayload(first);
  })();

  const shouldMirrorToConsole = globalThis.window !== undefined && !isConsoleLogger();

  if (payload) {
    timelineLogger.debug(message, payload);
    if (shouldMirrorToConsole) {
      logExtendedConsole('[Timeline]', message, payload);
    }
    return;
  }

  timelineLogger.debug(message);
  if (shouldMirrorToConsole) {
    logExtendedConsole('[Timeline]', message);
  }
}

function normalizePayload(value: unknown): Record<string, unknown> | undefined {
  if (value === undefined) {
    return;
  }

  const sanitized = sanitizeValue(value, 0, new WeakSet<object>());
  if (isRecord(sanitized)) {
    return sanitized;
  }

  return { value: sanitized } satisfies Record<string, unknown>;
}

function logExtendedConsole(prefix: string, message: string, payload?: Record<string, unknown>) {
  if (globalThis.window === undefined) {
    return;
  }

  if (!isExtendedLoggingEnabled()) {
    return;
  }

  if (payload && Object.keys(payload).length > 0) {
    console.log(`${prefix} ${message}:`, payload);
    return;
  }

  console.log(`${prefix} ${message}`);
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: sanitizer handles a wide set of JS runtime types.
function sanitizeValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (depth > 2) {
    if (typeof value === 'object') {
      const ctor = isRecord(value) ? getRecordValue(value, 'constructor') : null;
      const ctorName = isRecord(ctor) ? getRecordValue(ctor, 'name') : null;
      const label = typeof ctorName === 'string' && ctorName.length > 0 ? ctorName : 'unknown';
      return `[Object ${label}]`;
    }
    return value;
  }

  if (typeof value === 'string') {
    return value.length > 2000 ? `${value.slice(0, 2000)}… (truncated)` : value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'symbol') {
    return value.toString();
  }

  if (typeof value === 'function') {
    return `[function ${value.name || 'anonymous'}]`;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeValue(item, depth + 1, seen));
  }

  if (typeof value === 'object') {
    const object = value;

    if (seen.has(object)) {
      return '[Circular]';
    }

    seen.add(object);

    if (isDateObject(value)) {
      return value.toISOString();
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message,
        stack: value.stack,
      } satisfies Record<string, unknown>;
    }

    if (value instanceof Map) {
      return {
        type: 'Map',
        size: value.size,
        entries: [...value.entries()]
          .slice(0, 10)
          .map(([key, value_]) => [sanitizeValue(key, depth + 1, seen), sanitizeValue(value_, depth + 1, seen)]),
      } satisfies Record<string, unknown>;
    }

    if (value instanceof Set) {
      return {
        type: 'Set',
        size: value.size,
        values: [...value.values()].slice(0, 10).map((item) => sanitizeValue(item, depth + 1, seen)),
      } satisfies Record<string, unknown>;
    }

    if (globalThis.Blob && value instanceof Blob) {
      return {
        type: 'Blob',
        mimeType: value.type,
        size: value.size,
      } satisfies Record<string, unknown>;
    }

    if (!isRecord(object)) {
      return '[Object]';
    }

    const entries: Record<string, unknown> = {};
    let count = 0;
    for (const key of Object.keys(object)) {
      if (count >= 20) {
        // biome-ignore lint/complexity/useLiteralKeys: entries is a Record<string, unknown>.
        entries['__truncated__'] = true;
        break;
      }
      try {
        entries[key] = sanitizeValue(object[key], depth + 1, seen);
      } catch (error) {
        entries[key] = `[Unserializable: ${error instanceof Error ? error.message : 'unknown'}]`;
      }
      count += 1;
    }

    return entries;
  }

  return value;
}

export function logTimelineRealtime(event: string, details?: unknown) {
  if (!isExtendedLoggingEnabled()) {
    return;
  }

  const payload = normalizePayload(details);
  if (payload) {
    timelineLogger.debug(`[TimelineRealtime] ${event}`, payload);
    logExtendedConsole('[TimelineRealtime]', event, payload);
  } else {
    timelineLogger.debug(`[TimelineRealtime] ${event}`);
    logExtendedConsole('[TimelineRealtime]', event);
  }
}

export function logUserAnalysisRealtime(event: string, details?: unknown) {
  if (!isExtendedLoggingEnabled()) {
    return;
  }

  const payload = normalizePayload(details);
  if (payload) {
    analysisLogger.debug(`[UserAnalysisRealtime] ${event}`, payload);
    logExtendedConsole('[UserAnalysisRealtime]', event, payload);
  } else {
    analysisLogger.debug(`[UserAnalysisRealtime] ${event}`);
    logExtendedConsole('[UserAnalysisRealtime]', event);
  }
}

type DebugLogger = {
  debug: (message: string, data?: unknown) => void;
};

export function logExtendedDebug(scope: string, message: string, details?: unknown, loggerOverride?: DebugLogger) {
  if (!isExtendedLoggingEnabled()) {
    return;
  }

  const payload = normalizePayload(details);
  const prefix = `[${scope}]`;
  const targetLogger = loggerOverride ?? timelineLogger;

  if (payload) {
    targetLogger.debug(`${prefix} ${message}`, payload);
    logExtendedConsole(prefix, message, payload);
  } else {
    targetLogger.debug(`${prefix} ${message}`);
    logExtendedConsole(prefix, message);
  }
}
